import React, {useEffect, useState} from 'react'
import {useParams} from 'react-router-dom'
import {useQuery} from 'react-query'
import 'react-datepicker/dist/react-datepicker.css'
import {useSolv, useTenant} from "./components/SolvProvider"
import {useIntlEx} from "./components/IntlUtils.js"
import MainMenu from "./MainMenu.js"
import {
  ActionButton,
  BsCol,
  BsRow,
  DropdownMenu,
  DropdownMenuDivider,
  DropdownMenuItem,
  FormBody,
  FormGroup,
  FormGroups,
  FormHeader,
  MainContainer,
  Span,
  useAlert
} from "./components/FormComps.js"
import BroadcastDetailsDialog from "./BroadcastDetailsDialog.js"
import BroadcastCloneDialog from "./BroadcastCloneDialog.js"
import BroadcastDeleteDialog from "./BroadcastDeleteDialog.js"
import {
  BroadcastSummaryWidgets,
  BroadcastView,
  calcReachEstimates,
  useAdSystemOpts
} from "./components/BroadcastComps.js"
import {BroadcastStatusTagLabel} from "./components/BroadcastComps"
import {openDialog, openDialogCurried} from "./components/DialogUtils";
import {AccountTabs, mkHref} from "./components/AccountUtils";
import ExportToFileDialog from "./components/ExportToFileDialog";
import {BroadcastPublishDialog} from "./BroadcastPublishDialog";
import BroadcastStopDialog from "./BroadcastStopDialog";
import {useSearchParams} from "./components/UrlUtils";
import BroadcastApproveDialog from "./BroadcastApproveDialog";
import BroadcastOverrideDialog from "./BroadcastOverrideDialog";
import getLogger from "./components/Logging.js"
import "./App.css"

const log = getLogger("Broadcast")

export default function Broadcast() {

  const {api, session, setBusy, setFatal} = useSolv()
  const {intl} = useIntlEx()

  const [alert, setAlert] = useAlert()

  const params = useParams()

  const [broadcastId, setBroadcastId] = useState(params.broadcastId ? params.broadcastId : null)

  const {tenant} = useTenant(params.tenantId)

  const {findAdSystemOpt} = useAdSystemOpts()

  async function getBroadcast(tenantId, broadcastId) {
    try {
      const res = await api.getBroadcast(tenant.tenantId, params.broadcastId)
      // log.info(">>>Z: Broadcast: res=", res)
      if (res && res.data) {
        if (res.error) {
          log.info(">>>Z: Broadcast: error=", res.error)
          handleFatal(res.error)
        }
        else {
          res.data.reach = calcReachEstimates({...res.data, tenant})
          log.debug(">>>Z: broadcast.reach=", res.data.reach)
          return res.data
        }
      }
      else {
        log.info(">>>Z: Broadcast: error=Nothing")
        handleFatal(1)
      }
    }
    catch (error) {
      log.info(">>>Z: Broadcast: error=", error)
      handleFatal(error)
    }
  }

  const {data: broadcast, isLoading: broadcastLoading} = useQuery({
    queryFn: () => getBroadcast(tenant.tenantId, params.broadcastId),
    queryKey: ["broadcast", params.broadcastId],
    enabled: !!tenant && !!params.broadcastId
  })

  useEffect(() => {
    if (broadcastLoading) {
      setBusy(intl.msg("loading"))
    }
    else {
      setBusy(null)
    }
  }, [broadcastLoading])

  function handleEditClick(e) {
    if (canEdit()) {
      window.location = mkHref({suffix: `/broadcasts/${broadcastId}/_edit`, tenant: tenant, user: session?.user})
    }
  }

  function handleFatal(ex) {
    // log.info(">>>Z: handleFatal: ex=", JSON.stringify(ex))
    setFatal(ex)
    // if (ex.code) {
    //   switch (ex.code) {
    //     case "DUPLICATE_KEY":
    //       // // log.debug("DUPLICATE_ERROR")
    //       setAlert({type: "error", text: intl.msg("error_validation"), field: "inp_broadcastName", constraint: "unique"})
    //       // showError("inp_broadcastName", "unique")
    //       break
    //     default:
    //       setAlert({type: "error", text: intl.msg("error_failed")})
    //       break
    //   }
    // }
    // else {
    //   setAlert({type: "error", text: intl.msg("error_failed")})
    //   // setError(intl.fm("error_failed"))
    // }
  }

  async function handleRepublishClick() {

    setBusy(intl.msg("working"))
    try {

      const {data} = api.republishBroadcast(tenant.tenantId, broadcastId)

      window.location = mkHref({suffix: `/broadcasts`, tenant: tenant, user: session?.user})

    }
    catch (err) {
      handleFatal(err)
    }
    finally {
      setBusy(null)
    }
  }

  async function handleExport() {
    return await api.exportBroadcast(tenant.tenantId,{broadcastId: broadcastId})
  }

  function uploadFile(file) {
    const formData = new FormData()
    formData.append("file", file)
    const url = `/v1/tenants/${tenant.tenantId}/broadcasts/${broadcastId}/_import`
    return api.post(url, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
      withCredentials: true,
    })
  }

  async function handleImportClick(e) {
    console.log("handleImportClick: invoked")
    try {
      const files = e?.target?.files;
      // e.target.value = null;
      setAlert(null)
      console.log("handleImportClick: files=", files)
      if (files && files.length > 0) {
        setBusy(intl.msg("uploading"))
        const res = await uploadFile(files[0])
        window.location.reload()
      }
    }
    catch (error) {
      console.error("handleImportClick: error=", error)
      handleError(error)
    }
    finally {
      setBusy(null)
    }
  }

  function handleError(error) {
    if (error) {
      // log.debug("handleError: error=", JSON.stringify(error))
      if (error.code) {
        switch (error.code) {
          case "BAD_PAYLOAD":
            if (error.msg) {
              setAlert({type: "error", text: error.msg})
            }
            else {
              setAlert({type: "error", text: intl.msg("error_failed")})
            }
            break
          case "UNKNOWN_LOCATION":
            // log.debug("ERROR: ", error)
            if (error.msg) {
              setAlert({type: "error", text: error.msg})
            }
            else {
              setAlert({type: "error", text: intl.msg("error_failed")})
            }
            break
          default:
            setAlert({type: "error", text: intl.msg("error_failed")})
        }
      }
      else {
        setAlert({type: "error", text: intl.msg("error_failed")})
      }
    }
  }

  function isDraft() {
    return (broadcast && ["DRAFT"].includes(broadcast?.status))
  }

  function isSubmitted() {
    return (broadcast && ["SUBMITTED"].includes(broadcast?.status))
  }

  function isManualAdSystem() {
    return ("MANUAL" === broadcast?.adCampaignProps?.adCampaignAdSystem)
  }

  function canShowStats() {
    return (["ENDED", "PUBLISHED", "PAUSED", "STARTED", "STOPPED"].includes(broadcast?.status) || broadcast?.status?.startsWith("DELIVERING"))
  }

  function canEdit() {
    if (isDraft()) {
      return (tenant.accessingAs("OPERATOR/INSTREAM,DOWNSTREAM/*"))
    }
    else if (isManualAdSystem()) {
      return (["SUBMITTED", "PUBLISHED", "STARTED", "DELIVERING"].includes(broadcast.status) && (tenant.accessingAs("OPERATOR/*/SYSTEM")))
    }
    else {
      return false
    }
  }

  function canOverride() {
    return (["SUBMITTED", "PUBLISHED", "STARTED", "DELIVERING"].includes(broadcast?.status) && ("MANUAL" === broadcast?.adCampaignProps?.adCampaignAdSystem) && tenant.accessingAs("OPERATOR/*/SYSTEM"))
  }

  function canApprove() {
    return (isSubmitted() && ("MANUAL" === broadcast?.adCampaignProps?.adCampaignAdSystem) && tenant.accessingAs("OPERATOR/*/SYSTEM"))
  }

  function canReject() {
    return (isSubmitted() && ("MANUAL" === broadcast?.adCampaignProps?.adCampaignAdSystem) && tenant.accessingAs("OPERATOR/*/SYSTEM"))
  }

  function canDelete() {
    return ("DRAFT" === broadcast.status) && tenant.accessingAs("OPERATOR/INSTREAM,DOWNSTREAM/*")
    // return ("DRAFT" === broadcast.status) && (isBroadcastCreator() || tenant.accessingAs(["SUPERVISOR/INSTREAM,DOWNSTREAM/*"]))
  }

  function canClone() {
    return tenant.accessingAs(["OPERATOR/INSTREAM,DOWNSTREAM/*"])
  }

  function canStop() {
    return (["SUBMITTED", "PUBLISHED", "GENERATED", "IMPORTED", "STARTED"].includes(broadcast?.status) && (tenant.accessingAs(["SUPERVISOR/INSTREAM,DOWNSTREAM/*"])))
  }

  function canPublish() {
    return (
      ("DRAFT" === broadcast?.status)
      && !["TRIAL"].includes(tenant.tenantPlanId)
      && tenant.accessingAs("SUPERVISOR/INSTREAM,DOWNSTREAM/*")
    )
  }

  function MoreMenu(props) {
    const cmps = []

    if (tenant && broadcast) {

      if (canClone()) {

        cmps.push(
          <DropdownMenuItem name="clone" onClick={openDialogCurried("dlg_broadcast_clone")}/>
        )

        // if (canImport()) {
        //   cmps.push(
        //     <DropdownMenuItem id="mni_import" msg="import" icon="import" type="file" accept="text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" onClick={handleImportClick} withdivider/>
        //   )
        //   cmps.push(
        //     <DropdownMenuItem name="export" onClick={openDialogCurried("dlg_broadcast_export")}/>
        //   )
        // }
        // else {
          cmps.push(
            <DropdownMenuItem name="export" onClick={openDialogCurried("dlg_broadcast_export")} withdivider/>
          )
        // }
      }

      if (canStop()) {
        if (cmps.length > 0) {
          cmps.push(
            <DropdownMenuDivider/>
          )
        }
        cmps.push(
          <DropdownMenuItem name="stop" onClick={openDialogCurried("dlg_broadcast_stop")}/>
        )
      }

      if (canDelete()) {
        if (cmps.length > 0) {
          cmps.push(
            <DropdownMenuDivider/>
          )
        }
        cmps.push(
          <DropdownMenuItem name="delete" onClick={openDialogCurried("dlg_broadcast_delete")}/>
        )
      }

      if (cmps.length > 0) {
        cmps.push(
          <DropdownMenuDivider/>
        )
      }

      cmps.push(
        <DropdownMenuItem name="details" onClick={() => openDialog("dlg_broadcast_details")}/>
      )

    }
    return (
      cmps.length > 0 &&
        <DropdownMenu>
          {cmps}
        </DropdownMenu>
    )
  }

  function FormControls() {

    const ctls = []

    if (tenant && broadcast) {

      if ((broadcast.status === "PUBLISH_FAILED")) {
        if (tenant.accessingAs("MANAGER/*/SYSTEM")) {
          ctls.push(
            <ActionButton name="republish" flavor="primary" onClick={handleRepublishClick}/>
          )
        }
      }
      else if (tenant.accessingAs(["OPERATOR/INSTREAM,DOWNSTREAM/*"])) {
        if (canPublish()) {
          ctls.push(
            <ActionButton name="publish" flavor="primary" onClick={openDialogCurried("dlg_publish")}/>
          )
        }
        if (canEdit()) {
          ctls.push(
            <ActionButton name="edit" onClick={handleEditClick}/>
          )
        }
      }
      ctls.push(
        <MoreMenu/>
      )
    }
    return (ctls)
  }

  return (
    tenant &&
    <>

      <MainContainer tenant={tenant} menu={MainMenu}>

        <FormHeader>
          <AccountTabs tenant={tenant}/>
          <FormHeader.Toolbar>
            <FormHeader.Toolbar.Title>
              <div>
                {intl.msg("broadcast")}
              </div>
              <div className="text-muted">-</div>
              <div>
                {broadcast?.broadcastName}
              </div>
            </FormHeader.Toolbar.Title>

            <FormHeader.Toolbar.Controls>
              <FormControls/>
            </FormHeader.Toolbar.Controls>

          </FormHeader.Toolbar>

          <FormHeader.Alert alert={alert}/>

        </FormHeader>

        <FormBody>

          <FormGroup>
            <div className="d-flex flex-column justify-content-center align-items-center gap-3 w-100">
              <BroadcastStatusTagLabel broadcast={broadcast} tenant={tenant} user={session?.user} size="xl"/>
              {
                canShowStats() &&
                  <BroadcastSummaryWidgets broadcast={broadcast} tenant={tenant} isSystem={session?.user?.isSystemUser()}/>
              }
            </div>
          </FormGroup>

          {/*{*/}
          {/*  needShowStats() &&*/}
          {/*    <FormGroup>*/}
          {/*      <div className="d-flex flex-column justify-content-center align-items-center gap-3 w-100">*/}
          {/*        <BroadcastStatusTagLabel broadcast={broadcast} tenant={tenant} user={session?.user} size="lg"/>*/}
          {/*        <BroadcastSummaryWidgets broadcast={broadcast} tenant={tenant} isSystem={session?.user?.isSystemUser()}/>*/}
          {/*      </div>*/}
          {/*    </FormGroup>*/}
          {/*}*/}

          {
            broadcast && session?.user?.isSystemUser() && !isDraft() && broadcast?.adCampaignProps &&
              <FormGroups title={"Ad Campaign"} collapsed={true}>

                <BsRow>

                  <BsCol cols="9" breakpoint="sm">
                    <FormGroup>
                      <FormGroup.Label compact>
                        {intl.msg("ad_campaign")}
                      </FormGroup.Label>
                      <FormGroup.Control>
                        <div className="d-flex flex-row gap-1">
                          <span>
                            {findAdSystemOpt(broadcast?.adCampaignProps?.adCampaignAdSystem, {value: "UNDEF", label: "Undefined"}).label}:
                          </span>
                        {
                          ["AMOBEE", "NEXXEN"].includes(broadcast?.adCampaignProps?.adCampaignAdSystem) ? (
                            <a href={`https://platform.amobee.com/webapp/#/io-dashboard/${broadcast?.adCampaignProps?.adCampaignId}/lineItems`} className="text-link" style={{display: "block", overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis", width: "100%"}} target="_blank">
                              {broadcast?.adCampaignProps?.adCampaignName}
                            </a>
                          )
                        : (
                            <><span>{broadcast?.adCampaignProps?.adCampaignName}</span><Span hidden={!broadcast?.adCampaignProps?.adCampaignId}>({broadcast?.adCampaignProps?.adCampaignId})</Span></>
                          )
                        }
                        </div>
                      </FormGroup.Control>
                    </FormGroup>
                  </BsCol>

                </BsRow>

                <BsRow>

                  <BsCol cols="6" breakpoint="sm">
                    <FormGroup>
                      <FormGroup.Label compact>
                        {intl.msg("ad_schedule")}
                      </FormGroup.Label>
                      <FormGroup.Control>
                        {
                          broadcast?.adCampaignProps?.adCampaignStartTime &&
                            <div><span className="text-muted">From: </span><span>{intl.dateTime(new Date(broadcast?.adCampaignProps?.adCampaignStartTime))}</span></div>
                        }
                        {
                          broadcast?.adCampaignProps?.adCampaignEndTime &&
                            <div><span className="text-muted">To: </span><span>{intl.dateTime(new Date(broadcast?.adCampaignProps?.adCampaignEndTime))}</span></div>
                        }
                      </FormGroup.Control>
                    </FormGroup>
                  </BsCol>

                  <BsCol cols="6" breakpoint="sm">
                    <FormGroup>
                      <FormGroup.Label compact>
                        {intl.msg("ad_campaign_locations")}
                      </FormGroup.Label>
                      <FormGroup.Control>
                        {
                          broadcast?.targetLocationOpts
                            ?.map(t =>
                              "COORDS" === t.type ? <div>{(`Area(${t.lat}, ${t.lng}, ${t.radius})`)}</div>
                                : "LOCATION" === t.type ? <div>{(`Area(${t.amobeeCode?.mobileRadius?.entries[0]?.latLong?.latitude}, ${t.amobeeCode?.mobileRadius?.entries[0]?.latLong?.longitude}, ${t.amobeeCode?.mobileRadius?.radius})`)}</div>
                                  : <></>
                            )
                        }
                      </FormGroup.Control>
                    </FormGroup>
                  </BsCol>

                </BsRow>

                <BsRow>

                  <BsCol cols="3" breakpoint="sm">
                    <FormGroup cols="6" breakpoint="sm">
                      <FormGroup.Label compact>
                        {intl.msg("ad_budget")}
                      </FormGroup.Label>
                      <FormGroup.Control>
                        {intl.num(broadcast?.adCampaignProps.adCampaignBudget)}
                      </FormGroup.Control>
                    </FormGroup>
                  </BsCol>

                  <BsCol cols="3" breakpoint="sm">
                    <FormGroup>
                      <FormGroup.Label compact>
                        {intl.msg("ad_cpm")}
                      </FormGroup.Label>
                      <FormGroup.Control>
                        {intl.num(broadcast?.adCampaignProps.adCampaignCpm)}
                      </FormGroup.Control>
                    </FormGroup>
                  </BsCol>

                  <BsCol cols="3" breakpoint="sm">
                    <FormGroup cols="6" breakpoint="sm">
                      <FormGroup.Label compact>
                        {intl.msg("ad_bids")}
                      </FormGroup.Label>
                      <FormGroup.Control>
                        {broadcast?.adCampaignProps.adCampaignBids?.map(n => intl.num(n))?.join(", ")}
                      </FormGroup.Control>
                    </FormGroup>
                  </BsCol>

                  <BsCol cols="3" breakpoint="sm">
                    <FormGroup cols="6" breakpoint="sm">
                      <FormGroup.Label compact>
                        {intl.msg("ad_impression_cap")}
                      </FormGroup.Label>
                      <FormGroup.Control>
                        {intl.int(broadcast?.adCampaignProps.adCampaignImpressionCap)}
                      </FormGroup.Control>
                    </FormGroup>
                  </BsCol>

                </BsRow>

              </FormGroups>
          }

          <BroadcastView broadcast={broadcast} tenant={tenant} reachEstimates={broadcast?.reach}/>

        </FormBody>

      </MainContainer>

      {
        tenant && broadcast &&
          <>

            <BroadcastPublishDialog
              broadcast={broadcast}
              tenant={tenant}
              reachEstimates={broadcast?.reach}
            />

            <BroadcastDetailsDialog
              tenant={tenant}
              broadcast={broadcast}
              backdrop="overlay"
            />

            <BroadcastCloneDialog
              tenant={tenant}
              broadcastId={broadcast?.broadcastId}
              broadcastName={broadcast?.broadcastName}
            />

            <BroadcastStopDialog
              tenant={tenant}
              broadcastId={broadcast?.broadcastId}
              broadcastName={broadcast?.broadcastName}
            />

            <BroadcastDeleteDialog
              tenant={tenant}
              broadcastId={broadcast?.broadcastId}
              broadcastName={broadcast?.broadcastName}
            />

            <BroadcastApproveDialog
              tenant={tenant}
              broadcastId={broadcast?.broadcastId}
              broadcastName={broadcast?.broadcastName}
            />

            <BroadcastOverrideDialog
              tenant={tenant}
              broadcastId={broadcast?.broadcastId}
              broadcastName={broadcast?.broadcastName}
            />

            <ExportToFileDialog
              key="broadcast"
              type="broadcast"
              tenant={tenant}
              fileName={`${tenant?.tenantName} | ${broadcast?.broadcastName}`}
              fileExt={"csv"}
              onExport={handleExport}/>
          </>
      }
    </>
  )
}